/**
 * Store for managing Shopify Payouts Embedded state.
 * Handles payout list data, pagination, filtering, merchant context, and selection state.
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type {
  ShopifyPayoutSummary,
  ShopifyPayoutFilterParams,
  ShopifyPayoutDetail,
  ShopifyBalanceTransactionSummary,
  ShopifyBalanceTransactionProcessingState,
  ShopifyBankAccount,
} from '@/types/shopifyPayouts'
import type { AuthState } from '@/types/authState'
import { shopifyPayoutsEmbeddedApi } from '@/services/shopifyPayoutsEmbeddedService'
import { useMerchantStore } from '@/stores/merchantStore'
import { ApiError } from '@/util/ApiError'
import { normalizeError } from '@/util/errorNormalizer'

import { PERMISSIONS } from '@/config/permissions'
// @ts-expect-error ignore missing types
import { hasPerms } from '@/main'

/**
 * Build the cache key that uniquely identifies a loaded payout detail.
 *
 * The backend addresses a payout by the full
 * `store/{storeId}/business-entity/{businessEntityId}/payout/{payoutId}` tuple
 * — `payoutId` alone is NOT its identity. Two payouts in different business
 * entities can share a numeric `Id`, so the load cache must key on the same
 * tuple the API does or it would serve one entity's detail for the other.
 */
function payoutDetailKey(
  storeId: string,
  businessEntityId: string,
  payoutId: number,
): string {
  return `${storeId}|${businessEntityId}|${payoutId}`
}

/**
 * Get display name for a bank account associated with a payout.
 * Handles both ShopifyPayoutSummary and ShopifyPayoutDetail types.
 *
 * @param payout - The payout (summary or detail)
 * @param merchantId - The merchant ID to look up bank account info
 * @returns Formatted bank account display string, or null if not found
 */
function getBankAccountDisplayName(
  payout: ShopifyPayoutSummary | ShopifyPayoutDetail,
  merchantId: string | null | undefined,
): string | null {
  // Determine which account ID to use
  // For ASSIGNED payouts, prefer AssignedBankAccountId
  const accountId =
    payout.BankAccountAssignmentState === 'ASSIGNED' &&
    'AssignedBankAccountId' in payout &&
    payout.AssignedBankAccountId
      ? payout.AssignedBankAccountId
      : payout.BankAccountId ||
        ('AssignedBankAccountId' in payout
          ? payout.AssignedBankAccountId
          : null)

  if (!accountId) return null

  // Try to get merchant ID from payout if not provided (for pending payouts)
  const merchantIdToUse =
    merchantId || ('MerchantId' in payout ? payout.MerchantId : null)

  // Try to get bank account from merchant store first
  if (merchantIdToUse) {
    const merchantStore = useMerchantStore()
    const merchants = merchantStore.getMerchants()
    const merchant = merchants.find(m => m.Id === merchantIdToUse)

    if (merchant?.ShopifyStores) {
      for (const store of merchant.ShopifyStores) {
        if (!store.BusinessEntities) continue
        for (const entity of store.BusinessEntities) {
          if (!entity.BankAccounts) continue
          const account = entity.BankAccounts.find(acc => acc.Id === accountId)
          if (account) {
            const bankName = account.BankName || 'Bank Account'
            const lastFour = account.AccountNumberLastDigits || ''
            if (lastFour) {
              return `${bankName} •••• ${lastFour}`
            }
            return bankName
          }
        }
      }
    }
  }

  // Fallback: Check AvailableBankAccounts if this is a detail with that field
  if ('AvailableBankAccounts' in payout && payout.AvailableBankAccounts) {
    const account = payout.AvailableBankAccounts.find(
      acc => acc.Id === accountId,
    )
    if (account) {
      const bankName = account.BankName || 'Bank Account'
      const lastFour = account.LastFourDigits || ''
      if (lastFour) {
        return `${bankName} •••• ${lastFour}`
      }
      return bankName
    }
  }

  // Final fallback: show masked ID (never show full GID)
  return accountId.length > 8 ? `••••${accountId.slice(-4)}` : accountId
}

/**
 * Format transaction type string for display.
 * Converts snake_case to Title Case (e.g., "PAYMENT_REFUND" -> "Payment Refund").
 *
 * @param type - The transaction type string
 * @returns Formatted transaction type string, or '-' if empty
 */
function formatTransactionType(type: string): string {
  if (!type) return '-'
  return type
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase())
}

/**
 * Get display name for a business entity.
 *
 * @param businessEntityId - The business entity ID
 * @param merchantId - The merchant ID to look up business entity info
 * @returns Business entity display name, or the ID if not found
 */
function getBusinessEntityDisplayName(
  businessEntityId: string | null | undefined,
  merchantId: string | null | undefined,
): string {
  if (!businessEntityId || !merchantId) {
    return businessEntityId || '-'
  }

  const merchantStore = useMerchantStore()
  const merchants = merchantStore.getMerchants()
  const merchant = merchants.find(m => m.Id === merchantId)
  if (!merchant?.ShopifyStores) {
    return businessEntityId
  }

  for (const storeData of merchant.ShopifyStores) {
    if (!storeData.BusinessEntities) continue
    const entity = storeData.BusinessEntities.find(
      ent => ent.Id === businessEntityId,
    )
    if (entity) {
      return entity.DisplayName
    }
  }

  return businessEntityId
}

/**
 * Check if user has permission to assign bank accounts.
 *
 * @returns True if user has the required permission
 */
function hasAssignPermission(): boolean {
  return hasPerms(PERMISSIONS.SHOPIFY_PAYMENTS_UPDATE)
}

/**
 * Check if a bank account can be assigned to a payout.
 * Validates permission, assignment state, and payout status.
 *
 * @param payout - The payout to check (summary or detail)
 * @returns True if bank account can be assigned
 */
function canAssignBankAccount(
  payout: ShopifyPayoutSummary | ShopifyPayoutDetail,
): boolean {
  // Check permission
  if (!hasAssignPermission()) return false

  // Check assignment state
  if (payout.BankAccountAssignmentState !== 'PENDING_ASSIGNMENT') {
    return false
  }

  // Check payout status (only PAID or FAILED)
  return payout.Status === 'PAID' || payout.Status === 'FAILED'
}

export const useShopifyPayoutsStore = defineStore('shopifyPayouts', () => {
  // State
  const payouts = ref<ShopifyPayoutSummary[]>([])
  const isLoading = ref(false)
  const error = ref<ApiError | null>(null)
  const currentMerchantId = ref<string | null>(null)
  const currentMerchantName = ref<string | null>(null)
  const selectedMerchantId = ref<string | null>(null)

  // Pagination state
  const totalElements = ref(0)
  const totalPages = ref(0)
  const currentPage = ref(1)
  const pageSize = ref(20)

  // Filter state
  const filters = ref<ShopifyPayoutFilterParams>({})

  // Selection state
  const selectedPayout = ref<ShopifyPayoutSummary | null>(null)
  const selectedPayoutDetail = ref<ShopifyPayoutDetail | null>(null)
  const selectedBalanceTransaction =
    ref<ShopifyBalanceTransactionSummary | null>(null)

  // Payout detail loading state
  const isLoadingPayoutDetail = ref(false)
  const lastLoadedPayoutDetailKey = ref<string | null>(null)

  // Balance transactions state
  const balanceTransactions = ref<ShopifyBalanceTransactionSummary[]>([])
  const isLoadingBalanceTransactions = ref(false)
  const balanceTransactionsTotal = ref(0)
  const balanceTransactionsPageSize = ref(20)
  const balanceTransactionsCurrentPage = ref(1)
  const lastLoadedBalanceTransactionsPayoutId = ref<number | null>(null)
  const balanceTransactionIdFilter = ref<string | null>(null)
  const balanceTransactionProcessingStateFilter =
    ref<ShopifyBalanceTransactionProcessingState | null>(null)

  // Pending payouts state (all merchants)
  const pendingPayouts = ref<ShopifyPayoutSummary[]>([])
  const isLoadingPendingPayouts = ref(false)
  const pendingPayoutsTotalElements = ref(0)
  const pendingPayoutsTotalPages = ref(0)
  const pendingPayoutsCurrentPage = ref(1)
  const pendingPayoutsPageSize = ref(20)

  // Bank account assignment state
  const availableBankAccounts = ref<ShopifyBankAccount[]>([])
  const isLoadingBankAccounts = ref(false)
  const isAssigningBankAccount = ref(false)

  // Getters
  const hasPayouts = computed(() => payouts.value.length > 0)
  const paginationInfo = computed(() => ({
    page: currentPage.value,
    pageSize: pageSize.value,
    total: totalElements.value,
    totalPages: totalPages.value,
  }))

  const effectiveMerchantId = computed(
    () => selectedMerchantId.value || currentMerchantId.value,
  )

  // Actions
  async function fetchPayouts(
    authState: AuthState,
    merchantId: string,
    params?: ShopifyPayoutFilterParams,
  ) {
    isLoading.value = true
    error.value = null
    currentMerchantId.value = merchantId

    try {
      // Backend uses 0-based page index, convert from 1-based UI page
      const pageParam = params?.page ?? currentPage.value
      // Extract page and size from params to avoid overriding our conversion
      const { page, size, ...otherParams } = params || {}
      void page
      void size
      const response = await shopifyPayoutsEmbeddedApi.getPayouts(
        authState,
        merchantId,
        {
          page: pageParam - 1, // Convert to 0-based for backend
          size: params?.size ?? pageSize.value,
          ...filters.value,
          ...otherParams, // Spread other params but not page/size
        },
      )

      payouts.value = response.Content
      totalElements.value = response.TotalElements
      totalPages.value = response.TotalPages
      // Backend uses 0-based page index, convert to 1-based for UI
      currentPage.value = response.Number + 1
      pageSize.value = response.Size

      return response
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Prefetch payouts for a merchant in the background.
   * Does not set loading state so it doesn't trigger skeleton UI.
   * Silently swallows errors — this is a best-effort optimization.
   */
  async function prefetchPayouts(
    authState: AuthState,
    merchantId: string,
  ) {
    // Skip if already loaded for this merchant or currently loading
    if (currentMerchantId.value === merchantId && payouts.value.length > 0) {
      return
    }
    if (isLoading.value) return

    try {
      const response = await shopifyPayoutsEmbeddedApi.getPayouts(
        authState,
        merchantId,
        { page: 0, size: pageSize.value, sort: 'issuedAt,desc' },
      )

      // Only apply if no other fetch has happened in the meantime
      if (!isLoading.value) {
        currentMerchantId.value = merchantId
        payouts.value = response.Content
        totalElements.value = response.TotalElements
        totalPages.value = response.TotalPages
        currentPage.value = response.Number + 1
        pageSize.value = response.Size
      }
    } catch {
      // Silently fail — user will see the normal loading state when they open payouts
    }
  }

  function setFilters(newFilters: ShopifyPayoutFilterParams) {
    filters.value = newFilters
  }

  function resetFilters() {
    filters.value = {}
    currentPage.value = 1
  }

  function clearStore() {
    payouts.value = []
    currentMerchantId.value = null
    currentMerchantName.value = null
    selectedMerchantId.value = null
    totalElements.value = 0
    totalPages.value = 0
    currentPage.value = 1
    filters.value = {}
    error.value = null
    clearBalanceTransactions()
    clearSelection()
  }

  // Selection actions
  function setSelectedPayout(payout: ShopifyPayoutSummary | null) {
    selectedPayout.value = payout
    if (!payout) {
      selectedPayoutDetail.value = null
      selectedBalanceTransaction.value = null
    }
  }

  function setSelectedPayoutDetail(detail: ShopifyPayoutDetail | null) {
    selectedPayoutDetail.value = detail
  }

  /**
   * Set the selected detail AND prime the load cache so the next
   * `loadPayoutDetail` for the same payout short-circuits instead of
   * re-fetching. Use only when the detail was just fetched from the API (e.g.
   * the deep-link single-call lookup) and is known fresh. Generic callers that
   * may hold a stale/partial detail should use `setSelectedPayoutDetail` so the
   * load cache stays untouched.
   */
  function primeLoadedDetail(detail: ShopifyPayoutDetail) {
    selectedPayoutDetail.value = detail
    lastLoadedPayoutDetailKey.value = payoutDetailKey(
      detail.StoreId,
      detail.BusinessEntityId,
      detail.Id,
    )
  }

  function setSelectedBalanceTransaction(
    transaction: ShopifyBalanceTransactionSummary | null,
  ) {
    selectedBalanceTransaction.value = transaction
  }

  function clearSelection() {
    selectedPayout.value = null
    selectedPayoutDetail.value = null
    selectedBalanceTransaction.value = null
    lastLoadedPayoutDetailKey.value = null
  }

  function clearBalanceTransactions() {
    balanceTransactions.value = []
    balanceTransactionsTotal.value = 0
    balanceTransactionsCurrentPage.value = 1
    lastLoadedBalanceTransactionsPayoutId.value = null
    balanceTransactionIdFilter.value = null
    balanceTransactionProcessingStateFilter.value = null
  }

  function resetPayoutDetailCache() {
    lastLoadedPayoutDetailKey.value = null
  }

  // Merchant sync helper
  function syncMerchantId(merchantId: string | null, clearData = false) {
    if (!merchantId) return

    if (clearData) {
      clearStore()
    } else if (selectedMerchantId.value && selectedMerchantId.value !== merchantId) {
      // Only clear when switching from one merchant to a different one — that
      // makes the prior merchant's selection stale. The initial null -> merchant
      // transition is not a switch (nothing was selected yet), so clearing there
      // would wipe a selection/detail the route just resolved and force a
      // redundant re-fetch.
      clearSelection()
    }
    selectedMerchantId.value = merchantId
  }

  // Payout detail loading
  async function loadPayoutDetail(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    businessEntityId: string,
    payoutId: number,
  ): Promise<ShopifyPayoutDetail | null> {
    const key = payoutDetailKey(storeId, businessEntityId, payoutId)
    if (isLoadingPayoutDetail.value || lastLoadedPayoutDetailKey.value === key) {
      return selectedPayoutDetail.value
    }

    isLoadingPayoutDetail.value = true

    try {
      const detail = await shopifyPayoutsEmbeddedApi.getPayoutDetail(
        authState,
        merchantId,
        storeId,
        businessEntityId,
        payoutId,
      )
      selectedPayoutDetail.value = detail
      lastLoadedPayoutDetailKey.value = key
      return detail
    } catch (err) {
      error.value = normalizeError(err)
      lastLoadedPayoutDetailKey.value = null
      throw error.value
    } finally {
      isLoadingPayoutDetail.value = false
    }
  }

  function findPayoutById(payoutId: string | number) {
    const id = typeof payoutId === 'string' ? parseInt(payoutId, 10) : payoutId
    return payouts.value.find(p => p.Id === id) || null
  }

  // Balance transactions loading
  async function loadBalanceTransactions(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    businessEntityId: string,
    payoutId: number,
    page: number = 1,
    size?: number,
    sort?: string,
    processingState?: ShopifyBalanceTransactionProcessingState | null,
    id?: string,
  ) {
    // Prevent duplicate calls for the same payout, page, and filters
    const normalizedId =
      typeof id === 'string' && id.trim().length > 0 ? id.trim() : null
    const currentProcessingState =
      processingState ?? balanceTransactionProcessingStateFilter.value
    const currentId =
      normalizedId !== null ? normalizedId : balanceTransactionIdFilter.value
    if (
      isLoadingBalanceTransactions.value ||
      (lastLoadedBalanceTransactionsPayoutId.value === payoutId &&
        balanceTransactionsCurrentPage.value === page &&
        balanceTransactionIdFilter.value === currentId &&
        balanceTransactionProcessingStateFilter.value ===
          currentProcessingState)
    ) {
      return
    }

    isLoadingBalanceTransactions.value = true

    try {
      const response = await shopifyPayoutsEmbeddedApi.getBalanceTransactions(
        authState,
        merchantId,
        storeId,
        businessEntityId,
        payoutId,
        {
          page: page - 1, // Convert to 0-based for backend
          size: size ?? balanceTransactionsPageSize.value,
          sort: sort,
          id: currentId ?? undefined,
          processingState: currentProcessingState ?? undefined,
        },
      )

      balanceTransactions.value = response.Content
      balanceTransactionsTotal.value = response.TotalElements
      balanceTransactionsCurrentPage.value = response.Number + 1 // Convert to 1-based for UI
      balanceTransactionsPageSize.value = response.Size
      lastLoadedBalanceTransactionsPayoutId.value = payoutId
      balanceTransactionIdFilter.value = currentId ?? null

      return response
    } catch (err) {
      error.value = normalizeError(err)
      lastLoadedBalanceTransactionsPayoutId.value = null
      throw error.value
    } finally {
      isLoadingBalanceTransactions.value = false
    }
  }

  function setBalanceTransactionsPageSize(size: number) {
    balanceTransactionsPageSize.value = size
  }

  function setBalanceTransactionProcessingStateFilter(
    state: ShopifyBalanceTransactionProcessingState | null,
  ) {
    balanceTransactionProcessingStateFilter.value = state
    // Reset pagination when filter changes
    balanceTransactionsCurrentPage.value = 1
    // Reset cache to allow re-fetching with new filter
    lastLoadedBalanceTransactionsPayoutId.value = null
  }

  function setBalanceTransactionIdFilter(id: string | null) {
    const normalizedId =
      typeof id === 'string' && id.trim().length > 0 ? id.trim() : null
    balanceTransactionIdFilter.value = normalizedId
    // Reset pagination when filter changes
    balanceTransactionsCurrentPage.value = 1
    // Reset cache to allow re-fetching with new filter
    lastLoadedBalanceTransactionsPayoutId.value = null
  }

  // Fetch payouts pending bank account assignment
  async function fetchPendingPayouts(
    authState: AuthState,
    params?: ShopifyPayoutFilterParams,
  ) {
    isLoadingPendingPayouts.value = true

    try {
      const pageParam = params?.page ?? pendingPayoutsCurrentPage.value
      const { page, size, ...otherParams } = params || {}
      void page
      void size

      const response =
        await shopifyPayoutsEmbeddedApi.getPayoutsPendingBankAccountAssignment(
          authState,
          {
            page: pageParam - 1, // Convert to 0-based for backend
            size: params?.size ?? pendingPayoutsPageSize.value,
            ...otherParams,
          },
        )

      const merchantStore = useMerchantStore()
      const merchants = merchantStore.getMerchants()
      const enrichedPayouts = response.Content.map(payout => {
        if (payout.MerchantId) {
          const merchant = merchants.find(m => m.Id === payout.MerchantId)
          if (merchant) {
            return { ...payout, MerchantName: merchant.Name }
          }
        }
        return payout
      })

      pendingPayouts.value = enrichedPayouts
      pendingPayoutsTotalElements.value = response.TotalElements
      pendingPayoutsTotalPages.value = response.TotalPages
      pendingPayoutsCurrentPage.value = response.Number + 1
      pendingPayoutsPageSize.value = response.Size

      return response
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      isLoadingPendingPayouts.value = false
    }
  }

  // Fetch available bank accounts for a business entity
  async function fetchBankAccounts(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    businessEntityId: string,
  ) {
    isLoadingBankAccounts.value = true

    availableBankAccounts.value = []

    try {
      const merchantStore = useMerchantStore()
      const merchants = merchantStore.getMerchants()
      const merchant = merchants.find(m => m.Id === merchantId)
      const store = merchant?.ShopifyStores.find(s => s.Id === storeId)
      const businessEntity = store?.BusinessEntities?.find(
        be => be.Id === businessEntityId,
      )
      const merchantBankAccounts = businessEntity?.BankAccounts || []

      const mappedAccounts: ShopifyBankAccount[] = merchantBankAccounts.map(
        account => {
          let createdAt: string | undefined
          if (account.CreatedAt) {
            createdAt =
              typeof account.CreatedAt === 'string'
                ? account.CreatedAt
                : new Date(account.CreatedAt).toISOString()
          }

          return {
            Id: account.Id,
            IsReach: account.IsReach,
            CreatedAt: createdAt,
            BankName: account.BankName || undefined,
            LastFourDigits: account.AccountNumberLastDigits || undefined,
          }
        },
      )

      const uniqueAccounts = mappedAccounts.filter(
        (account, index, self) =>
          index === self.findIndex(a => a.Id === account.Id),
      )

      availableBankAccounts.value = uniqueAccounts
      return uniqueAccounts
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      isLoadingBankAccounts.value = false
    }
  }

  // Assign bank account to payout
  async function assignBankAccount(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    businessEntityId: string,
    payoutId: number,
    bankAccountId: string | null,
  ) {
    isAssigningBankAccount.value = true

    try {
      const updatedDetail =
        await shopifyPayoutsEmbeddedApi.assignBankAccountToPayout(
          authState,
          merchantId,
          storeId,
          businessEntityId,
          payoutId,
          {
            BankAccountId: bankAccountId,
          },
        )

      // Update selected payout detail if it matches
      if (
        selectedPayoutDetail.value &&
        selectedPayoutDetail.value.Id === payoutId
      ) {
        selectedPayoutDetail.value = updatedDetail
      }

      // Update payout in list if it exists
      const payoutInList = payouts.value.find(p => p.Id === payoutId)
      if (payoutInList) {
        payoutInList.BankAccountAssignmentState =
          updatedDetail.BankAccountAssignmentState
        payoutInList.AssignedBankAccountId = updatedDetail.AssignedBankAccountId
      }

      // Update pending payouts list if it exists
      const payoutInPendingList = pendingPayouts.value.find(
        p => p.Id === payoutId,
      )
      if (payoutInPendingList) {
        payoutInPendingList.BankAccountAssignmentState =
          updatedDetail.BankAccountAssignmentState
        payoutInPendingList.AssignedBankAccountId =
          updatedDetail.AssignedBankAccountId
      }

      // Refresh balance transactions after assignment
      // Balance transactions may need to be reprocessed after bank account assignment
      // Reset the cache to force a fresh fetch
      lastLoadedBalanceTransactionsPayoutId.value = null

      // If we're currently viewing this payout's balance transactions, reload them
      if (selectedPayoutDetail.value?.Id === payoutId) {
        try {
          await loadBalanceTransactions(
            authState,
            merchantId,
            storeId,
            businessEntityId,
            payoutId,
            balanceTransactionsCurrentPage.value,
          )
        } catch (error) {
          console.error(
            'Error refreshing balance transactions after assignment:',
            error,
          )
          // Don't throw - assignment was successful even if refresh failed
        }
      }

      return updatedDetail
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      isAssigningBankAccount.value = false
    }
  }

  // Clear bank account assignment state
  function clearBankAccountAssignmentState() {
    availableBankAccounts.value = []
    isLoadingBankAccounts.value = false
    isAssigningBankAccount.value = false
  }

  return {
    // State
    payouts,
    isLoading,
    error,
    currentMerchantId,
    currentMerchantName,
    selectedMerchantId,
    totalElements,
    totalPages,
    currentPage,
    pageSize,
    filters,
    selectedPayout,
    selectedPayoutDetail,
    selectedBalanceTransaction,
    isLoadingPayoutDetail,
    lastLoadedPayoutDetailKey,
    balanceTransactions,
    isLoadingBalanceTransactions,
    balanceTransactionsTotal,
    balanceTransactionsPageSize,
    balanceTransactionsCurrentPage,
    lastLoadedBalanceTransactionsPayoutId,
    balanceTransactionIdFilter,
    balanceTransactionProcessingStateFilter,
    pendingPayouts,
    isLoadingPendingPayouts,
    pendingPayoutsTotalElements,
    pendingPayoutsTotalPages,
    pendingPayoutsCurrentPage,
    pendingPayoutsPageSize,
    availableBankAccounts,
    isLoadingBankAccounts,
    isAssigningBankAccount,

    // Getters
    hasPayouts,
    paginationInfo,
    effectiveMerchantId,

    // Actions
    fetchPayouts,
    prefetchPayouts,
    setFilters,
    resetFilters,
    setSelectedPayout,
    setSelectedPayoutDetail,
    primeLoadedDetail,
    setSelectedBalanceTransaction,
    clearSelection,
    syncMerchantId,
    loadPayoutDetail,
    findPayoutById,
    resetPayoutDetailCache,
    loadBalanceTransactions,
    setBalanceTransactionsPageSize,
    setBalanceTransactionProcessingStateFilter,
    setBalanceTransactionIdFilter,
    fetchPendingPayouts,
    fetchBankAccounts,
    assignBankAccount,
    clearBankAccountAssignmentState,
    getBankAccountDisplayName,
    formatTransactionType,
    getBusinessEntityDisplayName,

    canAssignBankAccount,
  }
})
